import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import swal from 'sweetalert';
import { getRestPaymentInfo, getInterestPaymentInfo, initiateRestPayment } from '../../api/groupTourismApi';
import { PaymentMethodModal } from '../payments';
import { formatMoneyLegacy } from '../../utils/currency';
import { BASE_URL } from '../../constants';
import { Seo, PackagesGroupTripsBar, ContentGuidesStrip } from '../shared';
import BookingPageHero from '../booking/BookingPageHero';
import GroupsFormingNow from '../booking/GroupsFormingNow';
import GroupVendorDepartures from '../booking/GroupVendorDepartures';
import GroupDepartureHowItWorks from '../booking/GroupDepartureHowItWorks';
import GroupDepartureReserveFlow from '../booking/GroupDepartureReserveFlow';
import '../../styles/pkgs-gt-bar.scss';
import '../../styles/booking-experience.scss';

export function GroupTourismContent() {
	const [payRestInfo, setPayRestInfo] = useState(null);
	const [payRestIdx, setPayRestIdx] = useState(null);
	const [payRestPaying, setPayRestPaying] = useState(false);
	const [payInterestInfo, setPayInterestInfo] = useState(null);
	const [payInterestIdx, setPayInterestIdx] = useState(null);
	const [showPaymentModal, setShowPaymentModal] = useState(false);
	const [paymentModalData, setPaymentModalData] = useState(null);

	useEffect(() => {
		const q = new URLSearchParams(window.location.search);
		const pr = q.get('pay_rest');
		const pi = q.get('pay_interest');
		if (pr) {
			setPayRestIdx(pr);
			getRestPaymentInfo(pr)
				.then((r) => setPayRestInfo(r.data || { can_pay: false }))
				.catch(() => setPayRestInfo({ can_pay: false }));
		} else if (pi) {
			setPayInterestIdx(pi);
			getInterestPaymentInfo(pi)
				.then((r) => setPayInterestInfo(r.data || { can_pay: false }))
				.catch(() => setPayInterestInfo({ can_pay: false }));
		}
	}, []);

	const handlePayRest = () => {
		if (!payRestIdx || !payRestInfo?.can_pay || payRestPaying) return;
		setPayRestPaying(true);
		initiateRestPayment(payRestIdx)
			.then((r) => {
				if (r.data?.idx != null && r.data?.amount != null) {
					setPaymentModalData({
						productIdentity: r.data.idx,
						productName: 'GROUP_TOURISM_REST',
						productUrl: `${BASE_URL || ''}/group-trips`,
						amount: Number(r.data.amount),
						currencyCode: payRestInfo.currency_code || r.data.currency_code || 'NPR',
					});
					setShowPaymentModal(true);
				} else {
					swal('Error', 'Could not start payment.', 'error');
				}
			})
			.catch((e) => swal('Error', (e.response?.data?.errors || [])?.join(' ') || 'Could not start payment.', 'error'))
			.finally(() => setPayRestPaying(false));
	};

	const handlePayInterest = () => {
		if (!payInterestIdx || !payInterestInfo?.can_pay) return;
		setPaymentModalData({
			productIdentity: payInterestIdx,
			productName: 'GROUP_TOURISM',
			productUrl: `${BASE_URL || ''}/group-trips`,
			amount: Number(payInterestInfo.amount || 0),
			currencyCode: payInterestInfo.currency_code || 'NPR',
		});
		setShowPaymentModal(true);
	};

	return (
		<>
			<Seo
				title="Group Departures"
				path="/group-trips"
				noindex
				description="Join VisitAllNepal group departures — reserve with a deposit, we gather travellers, confirm the trip, and assign a verified local operator."
			/>
			<PackagesGroupTripsBar />
			<BookingPageHero
				title="Group Departures"
				subtitle="See groups forming now, join a confirmed departure, or start a new trek — reserve with a small deposit."
			/>

			<section className="booking-page" aria-label="Group departures booking">
				<div className="booking-page__inner container">
					<ContentGuidesStrip compact />
					{(payRestInfo || payInterestInfo) && (
						<div className="booking-page__payment-banner">
							{payRestInfo && (
								<div className={`booking-page__alert ${payRestInfo.can_pay ? 'booking-page__alert--ok' : 'booking-page__alert--warn'}`}>
									{payRestInfo.can_pay ? (
										<>
											<div>
												<strong>Complete your remaining payment</strong>
												<p>{payRestInfo.tour_name || 'Group departure'} — {formatMoneyLegacy(payRestInfo.amount ?? 0, payRestInfo.currency_code || 'NPR')}</p>
											</div>
											<button type="button" className="btn btn-success" onClick={handlePayRest} disabled={payRestPaying}>
												{payRestPaying ? 'Opening…' : 'Pay balance'}
											</button>
										</>
									) : (
										<p>Balance payment is not available for this link.</p>
									)}
								</div>
							)}
							{payInterestInfo && (
								<div className={`booking-page__alert ${payInterestInfo.can_pay ? 'booking-page__alert--ok' : 'booking-page__alert--warn'}`}>
									{payInterestInfo.can_pay ? (
										<>
											<div>
												<strong>Complete your deposit</strong>
												<p>{payInterestInfo.tour_name || 'Group departure'} — {formatMoneyLegacy(payInterestInfo.amount ?? 0, payInterestInfo.currency_code || 'NPR')}</p>
											</div>
											<button type="button" className="btn btn-success" onClick={handlePayInterest}>Pay deposit</button>
										</>
									) : (
										<p>Deposit payment is not available for this link.</p>
									)}
								</div>
							)}
						</div>
					)}

					<div className="booking-page__forming">
						<GroupsFormingNow />
					</div>

					<div className="booking-split booking-page__reserve">
						<div className="booking-split__main">
							<GroupDepartureReserveFlow />
						</div>
						<aside className="booking-split__aside">
							<GroupDepartureHowItWorks compact />
						</aside>
					</div>

					<GroupVendorDepartures />

					<p className="booking-page__alt">
						Prefer custom dates? <Link to="/packages">Request a private tour</Link>.
					</p>
				</div>
			</section>

			<PaymentMethodModal
				open={showPaymentModal}
				onClose={() => { setShowPaymentModal(false); setPaymentModalData(null); }}
				paymentParams={paymentModalData}
				amount={paymentModalData?.amount}
			/>
		</>
	);
}

export default function GroupTourismPage() {
	return <GroupTourismContent />;
}
